import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, Subscriber } from 'rxjs';
import { UserService } from '@origin8-web/core-ui/login/providers';
import { O8ConfigService } from '@origin8-web/core-ui/config';
import { ArcClientEnv } from '../../environments';
import { ConversationMessage, ConverseEvent, ToolConfirmation } from './converse.interfaces';

const CONVERSE_SUBSCRIPTION = `subscription ($input: ConverseInput!) {
  converse(input: $input) {
    __typename
    ... on ConverseTextDelta { messageId, delta }
    ... on ConverseToolCall { messageId, toolCallId, toolName, args, requiresConfirmation }
    ... on ConverseToolResult { messageId, toolCallId, toolName, result, isError }
    ... on ConverseStepStart { messageId, stepIndex }
    ... on ConverseStepFinish { messageId, stepIndex, finishReason }
    ... on ConverseData { messageId, dataType, data }
    ... on ConverseFinish { messageId, finishReason }
    ... on ConverseError { messageId, code, message }
  }
}`;

@Injectable({ providedIn: 'root' })
export class ConverseApi {
  readonly #userService = inject(UserService);
  readonly #configService = inject<O8ConfigService<ArcClientEnv>>(O8ConfigService);

  converse(
    messages: ConversationMessage[],
    toolConfirmations?: ToolConfirmation[],
    organizationContext?: string,
    retryConfirmedForTools?: string[],
  ): Observable<ConverseEvent> {
    return new Observable<ConverseEvent>((subscriber) => {
      const controller = new AbortController();
      this.#streamConverse(
        subscriber,
        controller,
        messages,
        toolConfirmations,
        organizationContext,
        retryConfirmedForTools,
      );
      return () => controller.abort();
    });
  }

  async #streamConverse(
    subscriber: Subscriber<ConverseEvent>,
    controller: AbortController,
    messages: ConversationMessage[],
    toolConfirmations?: ToolConfirmation[],
    organizationContext?: string,
    retryConfirmedForTools?: string[],
  ): Promise<void> {
    try {
      const token = await firstValueFrom(this.#userService.getAccessToken$());
      const url = this.#configService.get('securedGraphQlGateway');
      if (!url) {
        subscriber.error(new Error('GraphQL gateway URL not configured'));
        return;
      }

      const variables = {
        input: {
          messages,
          ...(toolConfirmations?.length ? { toolConfirmations } : {}),
          ...(organizationContext ? { organizationContext } : {}),
          ...(retryConfirmedForTools?.length ? { retryConfirmedForTools } : {}),
        },
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'multipart/mixed; boundary="graphql"; subscriptionSpec=1.0, application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query: CONVERSE_SUBSCRIPTION, variables }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        subscriber.error(new Error(`HTTP ${response.status}: ${response.statusText}\n${text}`));
        return;
      }

      const contentType = response.headers.get('content-type') ?? '';

      if (contentType.includes('multipart/mixed')) {
        await this.#parseMultipartStream(response.body!, subscriber);
      } else {
        const json = await response.json();
        if (json.data?.converse) {
          subscriber.next(json.data.converse as ConverseEvent);
        }
        if (json.errors) {
          subscriber.error(new Error(json.errors.map((e: { message: string }) => e.message).join('\n')));
          return;
        }
      }
      subscriber.complete();
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') {
        subscriber.complete();
      } else {
        subscriber.error(err);
      }
    }
  }

  async #parseMultipartStream(body: ReadableStream<Uint8Array>, subscriber: Subscriber<ConverseEvent>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        const { value } = result;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop()!;

        for (const line of lines) {
          this.#processLine(line, subscriber);
        }
      }

      if (buffer.trim()) {
        this.#processLine(buffer, subscriber);
      }
    } finally {
      reader.releaseLock();
    }
  }

  #processLine(line: string, subscriber: Subscriber<ConverseEvent>): void {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('--') || trimmed.startsWith('content-type')) return;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.payload?.data?.converse) {
        subscriber.next(parsed.payload.data.converse as ConverseEvent);
      } else if (parsed.payload?.errors) {
        subscriber.error(new Error(parsed.payload.errors.map((e: { message: string }) => e.message).join('\n')));
      }
    } catch {
      console.warn('Not JSON, skipping', trimmed);
      // not JSON, skip
    }
  }
}
